import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import { useQueryClient } from 'react-query'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { ApiPermission, AuthModel, UserModel } from './_models'
import * as authHelper from './AuthHelpers'
import { WithChildren } from '../../../../_metronic/helpers'
import { useAuthPermissions } from './permissions'
import { getProfile } from './_requests'

const PAGE_RESOURCE_MAP: Record<string, string[]> = {
  Dashboard: ['analytics'],
  'Mapped URLs': ['template_mapping'],
  'URL Verification': ['url'],
  'Account Statistics': ['analytics'],
  'Vendor List': ['ip_address'],
  'SMS Report': ['hop', 'route'],
  'Support Center': ['ticket'],
  'Roles Management': ['role'],
  'User Management': ['user'],
  'Global Blacklist': ['url', 'ip_address'],
  'KYC Verification': ['kyc'],
  'Sender Approval': ['sender'],
}

const LEGACY_TO_ACTION_MAP: Record<string, string> = {
  can_read: 'read',
  can_write: 'create',
  can_edit: 'update',
}

const hasApiPermission = (
  permissions: ApiPermission[] | undefined,
  page: string,
  permission: string
) => {
  const action = LEGACY_TO_ACTION_MAP[permission]
  const resources = PAGE_RESOURCE_MAP[page] || []

  if (!action || resources.length === 0 || !permissions?.length) {
    return false
  }

  return permissions.some(
    (item) => item?.scope === 'all' && resources.includes(item.resource) && item.action === action
  )
}

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
  checkPermission: (page: string, permission: string) => boolean
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => { },
  currentUser: undefined,
  setCurrentUser: () => { },
  logout: () => { },
  checkPermission: (page: string, permission: string) => {
    return false
  },
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const { data: apiPermissions } = useAuthPermissions(!!auth?.access_token)
  const queryClient = useQueryClient()

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    queryClient.clear()
    saveAuth(undefined)
    setCurrentUser(undefined)
  }

  const checkPermission = (page: string, permission: string) => {
    const temp = currentUser?.group_obj?.group_permissions?.filter(
      (ele) => ele?.permission?.name === page
    )
    if (temp?.length > 0 && temp[0][permission]) {
      return true
    }

    if (hasApiPermission(apiPermissions, page, permission)) {
      return true
    }

    return false
  }

  return (
    <AuthContext.Provider
      value={{ auth, saveAuth, currentUser, setCurrentUser, logout, checkPermission }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, logout, setCurrentUser } = useAuth()
  const { isLoading: permissionsLoading } = useAuthPermissions(!!auth?.access_token)
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          const { data } = await getProfile()
          setCurrentUser(data?.data)
        }
      } catch (error) {
        if (!didRequest.current) {
          setCurrentUser(auth?.user)
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.access_token) {
      requestUser()
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export { AuthProvider, AuthInit, useAuth }
